import React from 'react';
import { createPortal } from 'react-dom';
import { Check, Package, Wrench } from 'lucide-react';
import { UI_Z_INDEX } from '../../../utils/uiZIndex';
import type { LaborCostKind } from '../utils/offerLaborCostShare';

function NeutralIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 16 16" fill="none" className={className} aria-hidden>
      <circle cx="8" cy="8" r="5.25" stroke="currentColor" strokeWidth="1.5" />
      <path d="M5.25 8h5.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

const OPTIONS: Array<{ value: LaborCostKind; label: string; icon: React.ReactNode }> = [
  { value: 'neutral', label: 'Neutral', icon: <NeutralIcon className="h-3.5 w-3.5" /> },
  { value: 'leistung', label: 'Leistung', icon: <Wrench className="h-3.5 w-3.5" /> },
  { value: 'material', label: 'Material', icon: <Package className="h-3.5 w-3.5" /> },
];

const KIND_TITLE: Record<LaborCostKind, string> = {
  neutral: 'Kostenart: Neutral — nicht im Lohnkostenanteil',
  leistung: 'Kostenart: Leistung — zählt als Lohnkosten',
  material: 'Kostenart: Material — nicht im Lohnkostenanteil',
};

const PANEL_WIDTH = 168;
const PANEL_GAP = 6;
const VIEWPORT_MARGIN = 8;

export function OfferCostKindSwitcher({
  value,
  onChange,
  disabled,
}: {
  value: LaborCostKind;
  onChange: (next: LaborCostKind) => void;
  disabled?: boolean;
}) {
  const [open, setOpen] = React.useState(false);
  const btnRef = React.useRef<HTMLButtonElement>(null);
  const panelRef = React.useRef<HTMLDivElement>(null);
  const [pos, setPos] = React.useState<{ top?: number; bottom?: number; left: number } | null>(null);
  const active = value !== 'neutral';
  const currentIcon = OPTIONS.find((o) => o.value === value)?.icon ?? OPTIONS[0]!.icon;

  const updatePos = React.useCallback(() => {
    const el = btnRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    let left = rect.right - PANEL_WIDTH;
    if (left < VIEWPORT_MARGIN) left = VIEWPORT_MARGIN;
    if (left + PANEL_WIDTH > window.innerWidth - VIEWPORT_MARGIN) {
      left = Math.max(VIEWPORT_MARGIN, window.innerWidth - VIEWPORT_MARGIN - PANEL_WIDTH);
    }
    setPos({
      bottom: window.innerHeight - rect.top + PANEL_GAP,
      left,
    });
  }, []);

  React.useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      return;
    }
    updatePos();
    const onScroll = () => setOpen(false);
    const onResize = () => updatePos();
    document.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    return () => {
      document.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
  }, [open, updatePos]);

  React.useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (btnRef.current?.contains(t) || panelRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={KIND_TITLE[value]}
        title={KIND_TITLE[value]}
        onClick={() => !disabled && setOpen((o) => !o)}
        className={
          active
            ? 'inline-flex h-5 w-5 items-center justify-center rounded-md border border-blue-200 bg-blue-50 text-blue-600'
            : 'inline-flex h-5 w-5 items-center justify-center rounded-md text-slate-400 hover:bg-blue-50 hover:text-blue-500'
        }
      >
        {currentIcon}
      </button>
      {open && pos
        ? createPortal(
            <div
              ref={panelRef}
              role="listbox"
              aria-label="Kostenart"
              style={{
                position: 'fixed',
                top: pos.top,
                bottom: pos.bottom,
                left: pos.left,
                width: PANEL_WIDTH,
                zIndex: UI_Z_INDEX.portalDropdown,
              }}
              className="rounded-md border border-app-border bg-app-surface py-1 text-app-text shadow-lg"
            >
              {OPTIONS.map((opt) => {
                const selected = opt.value === value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    role="option"
                    aria-selected={selected}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      onChange(opt.value);
                      setOpen(false);
                    }}
                    className={
                      'flex w-full items-center gap-2 px-2.5 py-1.5 text-left text-[8.5pt] ' +
                      (selected
                        ? 'bg-pipedrive-blue/10 font-medium text-pipedrive-blue'
                        : 'text-app-text-muted hover:bg-app-hover')
                    }
                  >
                    <span className="inline-flex shrink-0 text-current" aria-hidden>
                      {opt.icon}
                    </span>
                    <span className="min-w-0 flex-1">{opt.label}</span>
                    {selected ? <Check className="h-3.5 w-3.5 shrink-0" aria-hidden /> : null}
                  </button>
                );
              })}
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
